import { Modal } from '../albums/Modal';
import { badgeIcon, type Badge, type BadgeList } from './api';

/**
 * Every badge, earned and not.
 *
 * ## Locked ones are shown, and shown as locked
 *
 * Hiding what somebody has not earned makes the shelf a mystery: there is no
 * way to find out that Explorer exists except by accidentally earning it. The
 * whole point of a badge is that you can want it first.
 *
 * Greyed and dimmed rather than replaced with a silhouette, because the
 * artwork is the incentive. A padlock where the picture should be tells you
 * there is something there; the picture tells you what.
 *
 * ## Progress only on your own
 *
 * A stranger's response carries earned badges and nothing else - see
 * `ListAchievementsAction`, which will not hand somebody's listening habits to
 * anybody who loads their profile. So `mine` decides whether there is anything
 * to draw a bar with, rather than the client deciding what to hide.
 */
export function BadgesDialog({ list, onClose }: { list: BadgeList; onClose: () => void }) {
  const earned = list.items.filter((badge) => badge.earned).length;

  return (
    <Modal title="Badges" onClose={onClose} wide>
      <div className="flex flex-col gap-4" data-badges-dialog>
        <p className="text-[13px] leading-relaxed text-soft">
          {list.mine
            ? `You have earned ${earned} of ${list.total}.`
            : `${earned} of ${list.total} earned.`}
        </p>

        <ul className="flex flex-col gap-2.5">
          {list.items.map((badge) => (
            <Row key={badge.slug} badge={badge} showProgress={list.mine} />
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function Row({ badge, showProgress }: { badge: Badge; showProgress: boolean }) {
  const pct = badge.target === 0 ? 0 : Math.min(100, (badge.progress / badge.target) * 100);

  /*
   * A bar only where there is something to fill.
   *
   * Half the badges have a target of one - you have played a track or you have
   * not - and a progress bar showing 0/1 says nothing the greyed-out artwork
   * did not already say.
   */
  const meaningful = showProgress && !badge.earned && badge.target > 1;

  return (
    <li
      data-badge-row={badge.slug}
      data-earned={badge.earned}
      className={`flex items-center gap-4 rounded-[var(--radius-card)] border p-3.5 ${
        badge.earned ? 'border-line bg-raised/40' : 'border-line'
      }`}
    >
      <img
        src={badgeIcon(badge.icon.small)}
        alt=""
        width={56}
        height={56}
        className={`h-14 w-14 shrink-0 ${badge.earned ? '' : 'opacity-30 grayscale'}`}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className={`text-sm font-semibold ${badge.earned ? 'text-ink' : 'text-soft'}`}>
            {badge.label}
          </span>
          {badge.earned && badge.earned_at && (
            <span className="font-mono text-[10px] text-ghost">{earnedOn(badge.earned_at)}</span>
          )}
        </div>

        <span className="text-[12px] leading-snug text-soft">
          {badge.earned ? badge.blurb : badge.how}
        </span>

        {meaningful && (
          <div className="mt-1 flex items-center gap-2.5">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-raised">
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-faint">
              {badge.progress.toLocaleString()} / {badge.target.toLocaleString()}
            </span>
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * The date it was earned, in the reader's locale.
 *
 * The server sends `YYYY-MM-DD HH:MM:SS.mmm` in UTC without a zone marker, so
 * the `T` and the `Z` are added rather than assumed - `Date.parse` on the raw
 * string is implementation-defined and reads it as local time in some engines.
 */
function earnedOn(at: string): string {
  const date = new Date(`${at.replace(' ', 'T')}Z`);

  return Number.isNaN(date.valueOf())
    ? ''
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
